import type { StepItem } from '@/components/ui/stepper';
import {
  CERTIFICATION_STAGES,
  type AvanceRow,
  type CertificationStage,
  type CertificationStageView,
  type CertificationStepStatus,
} from '@/features/certification/avance/api';
import { hasAcceptedSetRun } from '@/features/certification/avance/declarability';

/**
 * The SII's six certification steps as the SPA shows them (spec §7.2), and
 * where the current one sends the person.
 *
 * Pure on purpose: «Empezar aquí» and «Estado de certificación» both render
 * it, and the rules about which screen moves a step forward are the part worth
 * testing.
 *
 * Every step has a screen of the certification menu now. Steps 1 and 2 are
 * declared with «Declarar avance» (spec §0 F2). The Intercambio is NOT
 * verified by email as the 2009 manual says: the SII portal («Set de
 * Intercambio», verified 2026-10-23) hands out an envío to download and takes
 * three XML answers back, and that upload is the step's declaration. Muestras
 * impresas go to the SII by email, and the Declaración de Cumplimiento is
 * signed by the representante legal on the SII's site.
 */

export const CERTIFICATION_STAGE_LABELS: Record<CertificationStage, string> = {
  set_de_pruebas: 'Set de pruebas',
  simulacion: 'Simulación',
  intercambio: 'Intercambio de información',
  muestras_impresas: 'Muestras impresas',
  declaracion_cumplimiento: 'Declaración de cumplimiento',
  autorizado: 'Autorizado',
};

/**
 * The one prerequisite before the SII's six steps: the company, its
 * certificate and its CAFs. It is not an SII step, so it is never numbered with
 * them. The checklist of «Empezar aquí», the prerequisite row of «Estado de
 * certificación» and the certification menu all name it with this label.
 */
export const COMPANY_PREREQUISITE_LABEL = 'Empresa y credenciales';

/** Shown whenever the stage was estimated from Factit's records rather than read from the SII. */
export const LOCAL_STAGE_NOTICE = 'Estimado por Factit; aún no leemos el SII';

export interface StageAction {
  /** One or two sentences: what this step is and what to do in it. */
  explanation: string;
  /** The screen that moves it forward, when there is one. */
  link: { to: string; label: string } | null;
}

/**
 * What the current step asks for.
 *
 * Step 1 links to «Declarar avance» once any set of the current Set de Pruebas
 * has an accepted run ({@link hasAcceptedSetRun}), and to the Set de pruebas
 * before that: with nothing accepted, declaring is not the next thing to do,
 * emitting is. Step 2 does the same with «Declarar simulación», once the
 * Simulación row is declarable or already with the SII.
 */
export function stageAction(
  stage: CertificationStage,
  rows: readonly AvanceRow[],
): StageAction {
  switch (stage) {
    case 'set_de_pruebas':
      return hasAcceptedSetRun(rows)
        ? {
            explanation:
              'Cuando el SII acepta los sets, hay que declararlos en «Declarar avance». Factit lo hace por ti con un clic.',
            link: { to: '/certification/test-set/avance', label: 'Declarar el avance' },
          }
        : {
            explanation:
              'Emite los sets de tu set de pruebas y espera a que el SII los acepte sin reparos.',
            link: { to: '/certification/test-set', label: 'Ir al set de pruebas' },
          };
    case 'simulacion': {
      // Like step 1: once a simulación can be declared, or is with the SII,
      // the next thing is «Declarar simulación», not another emission.
      const row = rows.find((r) => r.kind === 'simulacion');
      if (row?.eligible) {
        return {
          explanation:
            'El SII aceptó tu simulación. Ahora hay que declararla en «Declarar avance». Factit lo hace por ti con un clic.',
          link: { to: '/certification/simulation/avance', label: 'Declarar la simulación' },
        };
      }
      if (row?.siiEstadoClass === 'in_review' || row?.siiEstadoClass === 'rejected') {
        return {
          explanation:
            row.siiEstadoClass === 'in_review'
              ? 'Declaraste la simulación y el SII la está revisando.'
              : 'El SII rechazó la simulación declarada. Envía una nueva y vuelve a declararla.',
          link: { to: '/certification/simulation/avance', label: 'Ver la declaración' },
        };
      }
      return {
        explanation:
          'Envía al SII un lote de 20 a 100 documentos que represente tu facturación real, con todos los tipos que estás certificando.',
        link: { to: '/certification/simulation', label: 'Ir a Simulación' },
      };
    }
    case 'intercambio':
      return {
        explanation:
          'Baja del portal del SII el envío de prueba, respóndelo en Factit y sube las tres respuestas en el SII. Subirlas es la declaración de este paso.',
        link: { to: '/certification/intercambio', label: 'Ir a Intercambio' },
      };
    case 'muestras_impresas':
      return {
        explanation:
          'Envía al SII los PDF de todo el set de pruebas y de diez documentos de la simulación.',
        link: { to: '/certification/muestras', label: 'Ir a Muestras impresas' },
      };
    case 'declaracion_cumplimiento':
      return {
        explanation:
          'El representante legal firma la declaración de cumplimiento de requisitos en el sitio del SII.',
        link: { to: '/certification/declaracion', label: 'Ver qué se declara' },
      };
    case 'autorizado':
      return {
        explanation: 'El SII autorizó a la empresa. Ya puedes emitir en producción.',
        link: null,
      };
  }
}

/**
 * The screen of the certification menu where each step is worked on. The
 * authorisation is the SII's alone, so it has none.
 */
export const CERTIFICATION_STAGE_SCREENS: Record<CertificationStage, string | null> = {
  set_de_pruebas: '/certification/test-set',
  simulacion: '/certification/simulation',
  intercambio: '/certification/intercambio',
  muestras_impresas: '/certification/muestras',
  declaracion_cumplimiento: '/certification/declaracion',
  autorizado: null,
};

export interface CertificationStepsOptions {
  /** Each step links to its screen (`CERTIFICATION_STAGE_SCREENS`). */
  linked?: boolean;
  /**
   * The current step carries its explanation. On by default; off where the
   * explanation is shown beside the stepper instead of inside it.
   */
  describeCurrent?: boolean;
}

/**
 * The six steps for `Stepper`, in the SII's order. The status comes from the
 * API's `steps`; a step it omits is placed relative to `current`, so a short
 * answer still renders six steps.
 */
export function certificationSteps(
  stage: CertificationStageView,
  rows: readonly AvanceRow[],
  { linked = false, describeCurrent = true }: CertificationStepsOptions = {},
): StepItem[] {
  const currentIndex = CERTIFICATION_STAGES.indexOf(stage.current);
  return CERTIFICATION_STAGES.map((key, index) => {
    const fromApi = stage.steps.find((step) => step.key === key)?.status;
    const status: CertificationStepStatus =
      fromApi ?? (index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'pending');
    const screen = CERTIFICATION_STAGE_SCREENS[key];
    return {
      id: key,
      label: CERTIFICATION_STAGE_LABELS[key],
      status,
      description:
        describeCurrent && status === 'current' ? stageAction(key, rows).explanation : undefined,
      ...(linked && screen ? { to: screen } : {}),
    };
  });
}
